/**
 * Instanciation du client Dash Lite — le point d'entrée unique vers l'API.
 *
 * Tout le site passe par ce client. Aucun `fetch` vers l'API n'est écrit
 * ailleurs : cela garantit qu'un seul endroit décide de la clé de site, du
 * token courant et de la gestion des erreurs.
 *
 * Deux libertés offertes par le SDK sont exploitées ici :
 *
 * 1. l'option `fetch`, qui permet de brancher une implémentation
 *    personnalisée — utilisée pour alimenter l'inspecteur d'API ;
 * 2. `setToken()`, qui rend le client indépendant de la façon dont on
 *    persiste la session (ici : `localStorage`).
 *
 * Dans un projet réel, ce fichier ferait dix lignes. Le reste est de
 * l'instrumentation pédagogique, clairement isolée.
 */

import { DashLiteClient } from '@dash-lite/sdk';
import { appConfig } from '@/config/env';
import { apiLog } from '@/lib/api-log';

/** Clé de stockage du token de session dans le navigateur. */
const TOKEN_STORAGE_KEY = 'docinfopro.token';

/**
 * Lit le token de session persisté.
 *
 * `localStorage` est indisponible en navigation privée sur certains
 * navigateurs : l'accès est donc protégé, et le site fonctionne alors
 * simplement sans persistance entre deux rechargements.
 */
export function readStoredToken(): string | null {
    try {
        return window.localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

/** Persiste (ou efface) le token de session. */
export function writeStoredToken(token: string | null): void {
    try {
        if (token) {
            window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
        } else {
            window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        }
    } catch {
        // Stockage indisponible : on continue sans persistance.
    }
}

/**
 * `fetch` instrumenté : il journalise chaque échange avant de déléguer au
 * `fetch` natif du navigateur.
 *
 * Deux précautions à noter si vous reprenez ce code :
 *
 * - la réponse est **clonée** avant lecture (`response.clone()`), car un
 *   corps de réponse ne peut être lu qu'une fois — le SDK doit encore
 *   pouvoir le consommer ;
 * - une erreur de lecture du journal ne doit jamais faire échouer l'appel
 *   applicatif : tout y est enveloppé de `try/catch`.
 */
export const instrumentedFetch: typeof fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method ?? 'GET';
    const headers = (init?.headers ?? {}) as Record<string, string>;

    let parsedBody: unknown;

    try {
        parsedBody = typeof init?.body === 'string' ? JSON.parse(init.body) : undefined;
    } catch {
        parsedBody = init?.body;
    }

    const logId = apiLog.start({
        method,
        url,
        // On n'affiche que la partie utile : « /companies/search?q=café ».
        endpoint: url.replace(/^.*\/api\/v1/, '') || '/',
        headers,
        body: parsedBody,
    });

    const startedAt = performance.now();

    try {
        const response = await fetch(input, init);
        const durationMs = Math.round(performance.now() - startedAt);

        try {
            const body = response.status === 204 ? null : await response.clone().json();
            const errorCode =
                body && typeof body === 'object' && 'error' in body
                    ? ((body as { error?: { code?: string } }).error?.code ?? null)
                    : null;

            apiLog.finish(logId, { status: response.status, durationMs, body, errorCode });
        } catch {
            // Réponse non-JSON (page d'erreur d'un proxy, par exemple).
            apiLog.finish(logId, {
                status: response.status,
                durationMs,
                body: null,
                errorCode: null,
            });
        }

        return response;
    } catch (error) {
        apiLog.fail(
            logId,
            Math.round(performance.now() - startedAt),
            error instanceof Error ? error.message : 'Requête interrompue',
        );

        // L'erreur remonte telle quelle : c'est le SDK, puis `presentError`,
        // qui décideront de ce que voit le visiteur.
        throw error;
    }
};

/**
 * Le client partagé par toute l'application.
 *
 * Il est créé une fois pour toutes : le module est un singleton naturel en
 * ESM. Le token est réinjecté au démarrage depuis `localStorage` pour qu'un
 * rechargement de page ne déconnecte pas le visiteur.
 */
export const dashlite = new DashLiteClient({
    siteKey: appConfig.siteKey,
    baseUrl: appConfig.apiUrl,
    token: readStoredToken() ?? undefined,
    fetch: instrumentedFetch,
});

/**
 * Applique un token au client **et** au stockage local, dans cet ordre.
 *
 * Passer `null` déconnecte : c'est ce qu'on fait sur `unauthenticated` et
 * `environment_mismatch`, deux codes qui signifient qu'un token stocké n'a
 * plus rien à faire là.
 */
export function applyToken(token: string | null): void {
    dashlite.setToken(token);
    writeStoredToken(token);
}
